import { useMemo, useState } from 'react';
import { Autocomplete, FormLabel, Stack, TextField } from '@mui/material';
import { states } from '@/data/@consts/states';

interface State {
  name: string;
  cities: string[];
}

interface AutocompleteStatesAndCitiesProps {
  onStateAndCityChange: (state: string, city: string) => void;
  initialState?: string;
  initialCity?: string;
  stateError?: string;
  cityError?: string;
  direction?: 'row' | 'column';
  disabled?: boolean;
}

export const AutocompleteStatesAndCities = ({
  onStateAndCityChange,
  initialState = '',
  initialCity = '',
  stateError = '',
  cityError = '',
  direction = 'row',
  disabled = false,
}: AutocompleteStatesAndCitiesProps) => {
  const [selectedState, setSelectedState] = useState(initialState);
  const [selectedCity, setSelectedCity] = useState(initialCity);

  const { statesOptions, citiesByState } = useMemo(() => {
    const statesOptions = states.map((state: State) => state.name);
    const citiesByState: Record<string, string[]> = {};

    states.forEach((state: State) => {
      citiesByState[state.name] = state.cities;
    });

    return { statesOptions, citiesByState };
  }, []);

  const handleStateChange = (_: unknown, newValue: string | null) => {
    const state = newValue || '';
    setSelectedState(state);
    setSelectedCity('');
    onStateAndCityChange(state, '');
  };

  const handleCityChange = (_: unknown, newValue: string | null) => {
    const city = newValue || '';
    setSelectedCity(city);
    onStateAndCityChange(selectedState, city);
  };

  return (
    <Stack direction={{ xs: 'column', md: direction }} gap={2} mb={2}>
      <Stack flex={1}>
        <FormLabel error={!!stateError}>Estado</FormLabel>
        <Autocomplete
          disabled={disabled}
          options={statesOptions}
          value={selectedState || null}
          onChange={handleStateChange}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Selecione o estado"
              error={!!stateError}
              helperText={stateError}
            />
          )}
        />
      </Stack>
      <Stack flex={1}>
        <FormLabel error={!!cityError}>Cidade</FormLabel>
        <Autocomplete
          options={citiesByState[selectedState] || []}
          value={selectedCity || null}
          onChange={handleCityChange}
          disabled={!selectedState}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Selecione a cidade"
              error={!!cityError}
              helperText={cityError}
            />
          )}
        />
      </Stack>
    </Stack>
  );
};
